import { useEffect, useMemo, useState } from "react";
import GeneralBuildingProperties from "./GeneralBuildingProperties";
import { useTranslation } from 'react-i18next';
import TechnicalEquipmentProperties from "./TechnicalEquipmentProperties";
import DecarbonizationProperties from "./plot-of-land/DecarbonisationProperties";
import SocioDemographicsProperties from "./plot-of-land/SocioDemographicsProperties";
import PoIProperties from "./plot-of-land/PoIProperties";
import PVPotentialProperties from "./building/PVPotentialProperties";
import EnergyAndCO2Properties from "./building/EnergyAndCO2Properties";
import { userAPI } from "../UserAPI";
import { Building, EntityQuery, is, Query } from "@the-real-insight/tri-model";
import { useLocation } from "react-router-dom";
import BuildingLocationProperties from "./building/BuildingLocationProperties";
import MetersTab from "./building/MetersTab";
import RentalUnitsPanel from "./building/RentalUnitsPanel";
import EntityDocumentsPanel from "../components/EntityDocumentsPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Building3DView from './building/Building3DView';
import { useOverlayBotPage } from '../components/overlay-bot/OverlayBotContext';
import {
    buildingFullAddressLine,
    buildingPostalCityLine,
    buildingStreetLine,
} from "../utilities/buildingFlatAddress";

export const BUILDING_TAB_KEYS = [
  "general",
  "location",
  "documents",
  "neighborhood",
  "poi",
  "sociodemographics",
  "technicalEquipment",
  "meters",
  "rentalUnits",
  "energyAndCO2Balance",
  "pvPotential",
  "decarbonization",
] as const;

const DEFAULT_BUILDING_TAB = BUILDING_TAB_KEYS[0];

function resolveBuildingTab(tabFromUrl: string | null): string {
  return tabFromUrl && (BUILDING_TAB_KEYS as readonly string[]).includes(tabFromUrl)
    ? tabFromUrl
    : DEFAULT_BUILDING_TAB;
}

class Properties {
  building: any;
  poisChanged?: (pois: any[]) => void;
}
export default function BuildingProperties(properties: Properties) {
  const { t } = useTranslation();
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [tab, setTab] = useState(() => resolveBuildingTab(searchParams.get("tab")));

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    const resolved = resolveBuildingTab(tabFromUrl);
    if (tabFromUrl && tabFromUrl !== resolved) {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", resolved);
      window.history.replaceState(null, "", url.toString());
    }
    setTab((current) => (current === resolved ? current : resolved));
  }, [searchParams]);
  const [buildings, setBuildings] = useState([]) as any;
  const [building, setBuilding] = useState() as any;
  useEffect(() => {
    if (properties.poisChanged) {
      properties.poisChanged([]);
    }
  }, [tab]);
  // Keyed on the identifiers, not on the props object: the parent hands in a new
  // object on every render, which restarted this query each time and replaced
  // `building` with a fresh object, re-triggering every tab's own loading.
  const buildingBusinessId = properties.building?.id;
  const buildingObjectId = properties.building?._id;
  useEffect(() => {
    let cancelled = false;
    const call = async () => {
      let newBuildings =
        properties.building?.id != null && String(properties.building.id).length > 0
          ? await userAPI.query(
              new Query(
                new EntityQuery(Building.type, [], undefined, is("id").equal(`${properties.building.id}`)),
              ),
            )
          : [];
      if (newBuildings.length === 0 && properties.building?._id) {
        newBuildings = await userAPI.query(
          new Query(
            new EntityQuery(Building.type, [], undefined, is("_id").equal(`${properties.building._id}`)),
          ),
        );
      }
      if (cancelled) {
        return;
      }
      // The address id is not unique: a tenant's copy shares it with the public buildings it was made
      // from. The building opened is the one with its `_id` — the first by address was a public record
      // without the tenant's floors (John-Skilton-Straße 2, Würzburg: floors shown, then gone).
      const own = newBuildings.find((b: any) => String(b?._id) === String(properties.building?._id));
      const chosen = own ?? newBuildings[0];
      setBuildings(newBuildings);
      if (chosen) {
        setBuilding(chosen);
      }
    };
    void call();
    return () => {
      cancelled = true;
    };
    // Keyed on the ids, not the object: `properties` is new on every parent render. Loading the same
    // building in a loop made the other tabs' requests (meter hierarchy) time out behind it (18.10.2026).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingBusinessId, buildingObjectId]);
  const setParams = (value: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', value);
    window.history.replaceState(null, '', url.toString());
    setTab(value);
  }
  /** Entity from props (EntityPage) until /query fills `building` state — avoids blank header and tabs. */
  const displayBuilding = building ?? properties.building;
  const patchBuilding = (patch: Record<string, unknown>) => {
    setBuilding((b: any) => {
      const base = b ?? properties.building;
      return base ? { ...base, ...patch } : b;
    });
  };
  // The assistant should know which building is on screen — otherwise "welche
  // Nutzung hat dieses Gebäude?" has no referent. The context is stable across
  // data updates (address changes, patches) so that refining the same building's
  // data doesn't reset the conversation thread.
  const overlayContext = useMemo(() => {
    const buildingId = displayBuilding?.id || displayBuilding?._id;
    if (!buildingId) return undefined;
    const address = buildingFullAddressLine(displayBuilding);
    return `Der Nutzer betrachtet im Portal das Gebäude "${address || buildingId}".`;
  }, [
    displayBuilding?.id,
    displayBuilding?._id,
    displayBuilding?.street,
    displayBuilding?.streetNumber,
    displayBuilding?.postalCode,
    displayBuilding?.city,
    displayBuilding?.country,
  ]);

  useOverlayBotPage({
    helpText: t('overlayBot.help.building'),
    context: overlayContext,
  });

  const labels = useMemo(() => {
    if (
      displayBuilding?.minLat != null &&
      displayBuilding?.maxLat != null &&
      displayBuilding?.minLon != null &&
      displayBuilding?.maxLon != null
    ) {
      return [
        {
          text: buildingFullAddressLine(displayBuilding) || displayBuilding.id || '',
          lon: 0.5 * (displayBuilding.minLon + displayBuilding.maxLon),
          lat: 0.5 * (displayBuilding.minLat + displayBuilding.maxLat),
        },
      ];
    }
    return [];
  }, [
    displayBuilding?.minLat,
    displayBuilding?.maxLat,
    displayBuilding?.minLon,
    displayBuilding?.maxLon,
    displayBuilding?.id,
    displayBuilding?.street,
    displayBuilding?.streetNumber,
    displayBuilding?.postalCode,
    displayBuilding?.city,
    displayBuilding?.country,
  ]);
  return (displayBuilding
    ?
    <div className="flex min-w-0 flex-col gap-4">
      <div className="min-w-0">
        {buildings.length === 1 ?
          <p className="text-sm text-muted-foreground">{buildings ? (() => {
            switch ((displayBuilding as any).type) {
              case "apartments": return t('buildingProperties.apartments');
              case "hotel": return t('buildingProperties.hotel');
              case "school": return t('buildingProperties.school');
              case "church": return t('buildingProperties.church');
              case "yes": return t('buildingProperties.building');
              default: return t('buildingProperties.building');
            }
          })()
            :
            ""}</p>
          :
          <p className="text-sm text-muted-foreground">Liegenschaft</p>
        }
        {displayBuilding
          ? (() => {
              const street = buildingStreetLine(displayBuilding);
              const postal = buildingPostalCityLine(displayBuilding);
              const full = buildingFullAddressLine(displayBuilding);
              // Die Adresse ist die Überschrift des Inhalts (der Seitentitel
              // "Gebäude" steht im SiteHeader). Kundenfarbe über die Variable
              // aus Theme.tsx, Fallback auf den Token — wie in Section.
              const headingClass =
                "mt-0.5 break-words text-xl font-bold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))] sm:text-2xl";
              if (street || postal) {
                return (
                  <>
                    {street ? (
                      <h2 className={headingClass}>{street}</h2>
                    ) : null}
                    {postal ? (
                      <p className="text-sm text-muted-foreground">{postal}</p>
                    ) : null}
                  </>
                );
              }
              return full ? <h2 className={headingClass}>{full}</h2> : null;
            })()
          : null}
        <p className="mt-1 break-words text-xs text-muted-foreground">
          {buildings.map((b: Building, index: number) =>
            <span key={(b as any)._id ?? b.id ?? index}>
              <span>{index > 0 ? ', ': ''}</span>
              <span>{b.id_osm ? b.id_osm : '-'}</span>
            </span>
          )}
        </p>
      </div>
      {/* min-w-0: die Reiterleiste scrollt intern (ui/tabs), statt das Panel
          auf schmalen Viewports horizontal aufzudrücken. */}
      <Tabs value={String(tab)} onValueChange={setParams} className="min-w-0">
        {/* Auf Touch-Viewports bekommen die Reiter die vom Layout-Kontrakt
            geforderten 44px Höhe, ohne die Desktop-Dichte zu ändern. */}
        <TabsList
          aria-label={t('buildingProperties.tabsLabel')}
          className="max-sm:[&_[role=tab]]:min-h-[44px]"
        >
          <TabsTrigger value="general">{t('buildingProperties.general')}</TabsTrigger>
          <TabsTrigger value="location">{t('buildingProperties.location')}</TabsTrigger>
          <TabsTrigger value="documents">{t('entityDocuments.tab')}</TabsTrigger>
          {/* Historically `neighborhood`, removed in 6bf87ea. Same key so
              remembered URLs land here again — but on real LoD2 surfaces now,
              not the prism the old Environment3DProperties extruded. */}
          <TabsTrigger value="neighborhood">{t('buildingProperties.view3D')}</TabsTrigger>
          <TabsTrigger value="poi">{t('ui.nearby')}</TabsTrigger>
          <TabsTrigger value="sociodemographics">Soziodemographie</TabsTrigger>
          <TabsTrigger value="technicalEquipment">{t('buildingProperties.technicalEquipment')}</TabsTrigger>
          <TabsTrigger value="meters">{t('buildingProperties.meters')}</TabsTrigger>
          <TabsTrigger value="rentalUnits">{t('buildingProperties.rentalUnits')}</TabsTrigger>
          <TabsTrigger value="energyAndCO2Balance">Energie- und CO2-Bilanz</TabsTrigger>
          <TabsTrigger value="pvPotential">PV Potenzial</TabsTrigger>
          <TabsTrigger value="decarbonization">Dekarbonisierung</TabsTrigger>
        </TabsList>
        <TabsContent value="general" className="mt-4">
          <GeneralBuildingProperties
            building={displayBuilding}
            onBuildingPatch={patchBuilding}
          ></GeneralBuildingProperties>
        </TabsContent>
        <TabsContent value="location" className="mt-4">
          <BuildingLocationProperties object={displayBuilding}></BuildingLocationProperties>
        </TabsContent>
        <TabsContent value="documents" className="mt-4">
          <EntityDocumentsPanel
            entityType="Building"
            entity={displayBuilding}
            onChange={(updated: any) => setBuilding(updated)}
          />
        </TabsContent>
        <TabsContent value="neighborhood" className="mt-4">
          <Building3DView
            building={displayBuilding}
            /* The one building the page is about gets its address floating
               over it. Its neighbours stay unlabelled — they are context, and
               labelling them would turn the view into a list. */
            labels={labels}
          ></Building3DView>
        </TabsContent>
        <TabsContent value="poi" className="mt-4">
          <PoIProperties object={displayBuilding} poisChanged={properties.poisChanged}></PoIProperties>
        </TabsContent>
        <TabsContent value="sociodemographics" className="mt-4">
          <SocioDemographicsProperties object={displayBuilding}></SocioDemographicsProperties>
        </TabsContent>
        <TabsContent value="technicalEquipment" className="mt-4">
          <TechnicalEquipmentProperties building={displayBuilding}></TechnicalEquipmentProperties>
        </TabsContent>
        <TabsContent value="meters" className="mt-4">
          <MetersTab building={displayBuilding} />
        </TabsContent>
        <TabsContent value="rentalUnits" className="mt-4">
          <RentalUnitsPanel building={displayBuilding} />
        </TabsContent>
        <TabsContent value="energyAndCO2Balance" className="mt-4">
          <EnergyAndCO2Properties building={displayBuilding}></EnergyAndCO2Properties>
        </TabsContent>
        <TabsContent value="pvPotential" className="mt-4">
          <PVPotentialProperties building={displayBuilding}></PVPotentialProperties>
        </TabsContent>
        <TabsContent value="decarbonization" className="mt-4">
          <DecarbonizationProperties object={displayBuilding}></DecarbonizationProperties>
        </TabsContent>
      </Tabs>
    </div >
    :
    <></>
  );
}
